import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowUpRight, Lightbulb, MoveHorizontal, RotateCcw } from "lucide-react";
import "./dataAnnotationDemo.css";

const W = 800;
const H = 500;

const OBJECTS = [
  {
    id: "car",
    label: "Car",
    color: "#ff6b00",
    conf: "0.98",
    type: "Bounding box · Detection",
    desc: "Vehicle position and size, so a model can judge distance and avoid collisions.",
    box: [60, 392, 200, 88],
    mask: "60,462 60,432 96,426 116,396 206,396 236,426 260,436 260,468 234,480 196,480 196,466 124,466 124,480 86,480",
    points: [[253, 440], [64, 440], [105, 462], [215, 462], [160, 398]],
    edges: [[2, 3], [2, 4], [3, 4]],
  },
  {
    id: "person",
    label: "Pedestrian",
    color: "#3de0ff",
    conf: "0.96",
    type: "Keypoints · Pose",
    desc: "Joints and posture show where a person is heading, not just where they stand.",
    box: [300, 262, 48, 138],
    mask: "324,262 336,266 338,282 346,292 346,340 338,344 338,400 314,400 312,344 302,340 302,292 310,282 312,266",
    points: [[324, 276], [310, 296], [338, 296], [306, 336], [342, 336], [324, 346], [317, 372], [331, 372], [317, 398], [331, 398]],
    edges: [[0, 1], [0, 2], [1, 2], [1, 3], [2, 4], [1, 5], [2, 5], [5, 6], [5, 7], [6, 8], [7, 9]],
  },
  {
    id: "light",
    label: "Traffic light",
    color: "#b8ff3d",
    conf: "0.99",
    type: "Bounding box · Classification",
    desc: "Small objects with a state attached: the model must read red, amber or green.",
    box: [400, 108, 44, 112],
    mask: "404,110 440,110 440,214 425,216 425,220 420,220 420,216 404,214",
    points: [[422, 130], [422, 160], [422, 190]],
    edges: [[0, 1], [1, 2]],
  },
  {
    id: "cyclist",
    label: "Cyclist",
    color: "#ff4fd8",
    conf: "0.94",
    type: "Polygon · Segmentation",
    desc: "Rider and bike are one moving object. Tight outlines keep them from blending into the background.",
    box: [390, 380, 120, 100],
    mask: "392,456 400,430 436,426 446,414 458,396 460,380 474,380 476,398 470,410 486,420 508,440 510,462 496,480 476,480 440,480 400,480",
    points: [[466, 390], [462, 404], [452, 424], [466, 436], [450, 448], [480, 420], [418, 456], [486, 456]],
    edges: [[0, 1], [1, 2], [2, 3], [3, 4], [1, 5]],
  },
  {
    id: "truck",
    label: "Truck",
    color: "#ffd23c",
    conf: "0.97",
    type: "Bounding box · Detection",
    desc: "Large vehicles need their own class: they brake, turn and block the view differently.",
    box: [560, 330, 210, 150],
    mask: "560,330 700,330 700,370 750,370 770,410 770,462 750,480 600,480 580,480 560,462",
    points: [[600, 462], [660, 462], [738, 462], [735, 372], [560, 332], [766, 434]],
    edges: [],
  },
];

const BUILDINGS = [
  [0, 120, 110, 220], [110, 170, 90, 170], [200, 90, 120, 250],
  [320, 160, 100, 180], [420, 60, 130, 280], [550, 140, 110, 200], [660, 100, 140, 240],
];

const WINDOWS = BUILDINGS.flatMap(([bx, by, bw, bh], b) => {
  const out = [];
  const cols = Math.floor((bw - 16) / 20);
  const rows = Math.floor((bh - 40) / 28);
  for (let r = 0; r < rows; r += 1) {
    for (let c = 0; c < cols; c += 1) {
      out.push({
        key: `${b}-${r}-${c}`,
        x: bx + 10 + c * 20,
        y: by + 14 + r * 28,
        lit: (c * 7 + r * 13 + b * 5) % 5 < 2,
      });
    }
  }
  return out;
});

const LAYER_OPTIONS = [
  { id: "boxes", label: "Boxes" },
  { id: "masks", label: "Masks" },
  { id: "points", label: "Keypoints" },
];

const iou = (a, b) => {
  const x1 = Math.max(a[0], b[0]);
  const y1 = Math.max(a[1], b[1]);
  const x2 = Math.min(a[0] + a[2], b[0] + b[2]);
  const y2 = Math.min(a[1] + a[3], b[1] + b[3]);
  const inter = Math.max(0, x2 - x1) * Math.max(0, y2 - y1);
  const union = a[2] * a[3] + b[2] * b[3] - inter;
  return union > 0 ? inter / union : 0;
};

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
const Scene = ({ viewBox = `0 0 ${W} ${H}` }) => (
  <svg
    className="da__svg"
    viewBox={viewBox}
    preserveAspectRatio="xMidYMid slice"
    role="img"
    aria-label="Night street scene with a car, truck, pedestrian, cyclist and traffic light"
  >
    <defs>
      <linearGradient id="da-sky" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#0c1322" />
        <stop offset="1" stopColor="#2c3b55" />
      </linearGradient>
      <linearGradient id="da-road" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#1d212b" />
        <stop offset="1" stopColor="#0f1115" />
      </linearGradient>
      <radialGradient id="da-glow">
        <stop offset="0" stopColor="#ff4a4a" stopOpacity="0.5" />
        <stop offset="1" stopColor="#ff4a4a" stopOpacity="0" />
      </radialGradient>
      <filter id="da-grain">
        <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="2" stitchTiles="stitch" />
        <feColorMatrix values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.08 0" />
      </filter>
    </defs>

    <rect width={W} height={H} fill="url(#da-sky)" />

    {BUILDINGS.map(([x, y, w, h], i) => (
      <rect key={i} x={x} y={y} width={w} height={h} fill={i % 2 ? "#151d2e" : "#1a2438"} />
    ))}
    {WINDOWS.map((w) => (
      <rect
        key={w.key}
        x={w.x}
        y={w.y}
        width="9"
        height="13"
        rx="1.5"
        fill={w.lit ? "#ffc46b" : "#1f2a40"}
        opacity={w.lit ? 0.6 : 1}
      />
    ))}

    <rect y="320" width={W} height="80" fill="#2a3242" />
    <rect y="396" width={W} height="6" fill="#3a4357" />
    <rect y="402" width={W} height="98" fill="url(#da-road)" />
    {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
      <rect key={i} x={i * 100 + 20} y="486" width="56" height="5" fill="#586178" opacity="0.7" />
    ))}

    <ellipse cx="422" cy="132" rx="46" ry="46" fill="url(#da-glow)" />
    <rect x="420" y="214" width="5" height="108" fill="#2a3140" />
    <rect x="404" y="110" width="36" height="104" rx="8" fill="#171c26" stroke="#38425a" />
    <circle cx="422" cy="130" r="10" fill="#ff4a4a" />
    <circle cx="422" cy="160" r="10" fill="#4a3d1c" />
    <circle cx="422" cy="190" r="10" fill="#1d3a2a" />

    <rect x="312" y="344" width="10" height="56" rx="4" fill="#2b3345" />
    <rect x="326" y="344" width="10" height="56" rx="4" fill="#2b3345" />
    <rect x="303" y="294" width="7" height="44" rx="3" fill="#7d5042" />
    <rect x="338" y="294" width="7" height="44" rx="3" fill="#7d5042" />
    <rect x="308" y="288" width="32" height="60" rx="10" fill="#9a5f4c" />
    <circle cx="324" cy="276" r="12" fill="#c9a98a" />

    <path d="M94 428 L116 396 L206 396 L236 428 Z" fill="#46567a" />
    <path d="M124 426 L137 405 L168 405 L168 426 Z M176 426 L176 405 L200 405 L218 426 Z" fill="#17202f" />
    <rect x="60" y="424" width="200" height="40" rx="12" fill="#3a4a68" />
    <rect x="248" y="434" width="10" height="9" rx="2" fill="#ffe9a8" />
    <rect x="62" y="434" width="8" height="9" rx="2" fill="#ff5a4a" />
    <circle cx="105" cy="462" r="18" fill="#0b0d11" />
    <circle cx="105" cy="462" r="8" fill="#3a4150" />
    <circle cx="215" cy="462" r="18" fill="#0b0d11" />
    <circle cx="215" cy="462" r="8" fill="#3a4150" />

    <rect x="560" y="330" width="140" height="122" fill="#4b5870" />
    <rect x="575" y="350" width="110" height="6" fill="#64738f" opacity="0.7" />
    <rect x="700" y="370" width="70" height="82" rx="9" fill="#38445a" />
    <rect x="728" y="380" width="36" height="28" rx="3" fill="#17202f" />
    <rect x="560" y="450" width="210" height="12" fill="#1b1f28" />
    <rect x="763" y="428" width="7" height="10" rx="2" fill="#ffe9a8" />
    {[600, 660, 738].map((cx) => (
      <g key={cx}>
        <circle cx={cx} cy="462" r="18" fill="#0b0d11" />
        <circle cx={cx} cy="462" r="8" fill="#3a4150" />
      </g>
    ))}

    <g fill="none" stroke="#8f99ad" strokeWidth="4" strokeLinecap="round">
      <circle cx="418" cy="456" r="24" />
      <circle cx="486" cy="456" r="24" />
      <path d="M418 456 L446 426 L486 456 M446 426 L466 426" />
    </g>
    <path d="M452 424 L462 398" stroke="#e0833f" strokeWidth="11" strokeLinecap="round" />
    <path d="M462 402 L478 418 M452 424 L466 436 L450 448" stroke="#e0833f" strokeWidth="5" strokeLinecap="round" fill="none" />
    <circle cx="466" cy="390" r="10" fill="#c9a98a" />

    <rect width={W} height={H} filter="url(#da-grain)" />
  </svg>
);

const pct = (v, total) => `${(v / total) * 100}%`;

const Annotations = ({ items, layers, hot, onHot, tagFor, animateFrom }) => (
  <>
    {(layers.masks || layers.points) && (
      <svg className="da__vec" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden="true">
        {items.map((o) => (
          <g key={o.id} style={{ "--c": o.color }} className={hot === o.id ? "is-hot" : ""}>
            {layers.masks && <polygon className="da__mask" points={o.mask} />}
            {layers.points && (
              <>
                {o.edges.map(([a, b]) => (
                  <line
                    key={`${a}-${b}`}
                    className="da__edge"
                    x1={o.points[a][0]}
                    y1={o.points[a][1]}
                    x2={o.points[b][0]}
                    y2={o.points[b][1]}
                  />
                ))}
                {o.points.map(([x, y], i) => (
                  <circle key={i} className="da__kp" cx={x} cy={y} r="4" />
                ))}
              </>
            )}
          </g>
        ))}
      </svg>
    )}

    {items.map((o) => {
      const [x, y, w, h] = o.box;
      const from = animateFrom?.[o.id];
      const style = {
        "--c": o.color,
        left: pct(x, W),
        top: pct(y, H),
        width: pct(w, W),
        height: pct(h, H),
      };
      if (from) {
        style["--fl"] = pct(from[0], W);
        style["--ft"] = pct(from[1], H);
        style["--fw"] = pct(from[2], W);
        style["--fh"] = pct(from[3], H);
      }
      return (
        <div
          key={o.id}
          className={`da__box${layers.boxes ? "" : " is-quiet"}${hot === o.id ? " is-hot" : ""}${
            from ? " is-snapping" : ""
          }`}
          style={style}
          onPointerEnter={(e) => e.pointerType === "mouse" && onHot(o.id)}
          onPointerLeave={(e) => e.pointerType === "mouse" && onHot(null)}
        >
          <i className="da__h da__h--tl" />
          <i className="da__h da__h--tr" />
          <i className="da__h da__h--bl" />
          <i className="da__h da__h--br" />
          <span className="da__tag">{tagFor(o)}</span>
        </div>
      );
    })}
  </>
);

const DataAnnotationDemo = () => {
  const stageRef = useRef(null);
  const coordRef = useRef(null);
  const dragging = useRef(false);
  const drawStart = useRef(null);
  const introRef = useRef({ frame: 0, cancelled: false });

  const [mode, setMode] = useState("compare"); 
  const [pos, setPos] = useState(100);
  const [layers, setLayers] = useState({ boxes: true, masks: false, points: false });
  const [hot, setHot] = useState(null);
  const [done, setDone] = useState({}); 
  const [draft, setDraft] = useState(null);
  const [miss, setMiss] = useState(null);
  const [hint, setHint] = useState(null);

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const state = introRef.current;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        if (reduce) {
          setPos(50);
          return;
        }
        const t0 = performance.now();
        const run = (now) => {
          if (state.cancelled) return;
          const p = Math.min((now - t0 - 500) / 2200, 1);
          if (p >= 0) {
            const eased = 1 - Math.pow(1 - p, 3);
            setPos(100 - 52 * eased);
          }
          if (p < 1) state.frame = requestAnimationFrame(run);
        };
        state.frame = requestAnimationFrame(run);
      },
      { threshold: 0.45 }
    );
    io.observe(el);

    return () => {
      io.disconnect();
      state.cancelled = true;
      cancelAnimationFrame(state.frame);
    };
  }, []);

  const stopIntro = () => {
    introRef.current.cancelled = true;
    cancelAnimationFrame(introRef.current.frame);
  };

  const toScene = (e) => {
    const r = stageRef.current.getBoundingClientRect();
    return [((e.clientX - r.left) / r.width) * W, ((e.clientY - r.top) / r.height) * H, r];
  };

  const rectFrom = (a, x, y) => [
    Math.min(a[0], x),
    Math.min(a[1], y),
    Math.abs(x - a[0]),
    Math.abs(y - a[1]),
  ];

  const onPointerDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    const [x, y] = toScene(e);
    stageRef.current.setPointerCapture(e.pointerId);
    if (mode === "compare") {
      stopIntro();
      dragging.current = true;
      setPos(clamp((x / W) * 100, 0, 100));
    } else {
      drawStart.current = [x, y];
      setMiss(null);
      setDraft([x, y, 0, 0]);
    }
  };

  const onPointerMove = (e) => {
    const [x, y, r] = toScene(e);
    const el = stageRef.current;
    el.style.setProperty("--cx", `${e.clientX - r.left}px`);
    el.style.setProperty("--cy", `${e.clientY - r.top}px`);
    if (coordRef.current) {
      coordRef.current.textContent = `x ${String(Math.round(clamp(x, 0, W))).padStart(3, "0")}  y ${String(
        Math.round(clamp(y, 0, H))
      ).padStart(3, "0")}`;
    }
    if (mode === "compare" && dragging.current) {
      setPos(clamp((x / W) * 100, 0, 100));
    } else if (mode === "try" && drawStart.current) {
      setDraft(rectFrom(drawStart.current, clamp(x, 0, W), clamp(y, 0, H)));
    }
  };

  const onPointerUp = () => {
    dragging.current = false;
    if (mode !== "try" || !drawStart.current) return;
    drawStart.current = null;
    const rect = draft;
    setDraft(null);
    if (!rect || rect[2] < 14 || rect[3] < 14) return;

    let best = null;
    OBJECTS.forEach((o) => {
      const score = iou(rect, o.box);
      if (!best || score > best.score) best = { o, score };
    });

    if (best && best.score >= 0.3 && !done[best.o.id]) {
      setDone((d) => ({ ...d, [best.o.id]: { iou: best.score, from: rect } }));
      setHint(null);
    } else if (best && done[best.o.id] && best.score >= 0.3) {
      setMiss({ rect, text: "Already labelled" });
      setTimeout(() => setMiss(null), 1300);
    } else {
      setMiss({ rect, text: "Draw closer around one object" });
      setTimeout(() => setMiss(null), 1300);
    }
  };

  const onSliderKey = (e) => {
    if (e.key === "ArrowLeft") {
      stopIntro();
      setPos((p) => clamp(p - 4, 0, 100));
    }
    if (e.key === "ArrowRight") {
      stopIntro();
      setPos((p) => clamp(p + 4, 0, 100));
    }
  };

  const toggleLayer = (id) => setLayers((l) => ({ ...l, [id]: !l[id] }));

  const switchMode = (next) => {
    stopIntro();
    setMode(next);
    setHot(null);
    setDraft(null);
    setMiss(null);
    drawStart.current = null;
    if (next === "compare" && pos > 95) setPos(50);
  };

  const reset = () => {
    setDone({});
    setHint(null);
    setMiss(null);
  };

  const showHint = () => {
    const next = OBJECTS.find((o) => !done[o.id]);
    if (!next) return;
    setHint(next.id);
    setTimeout(() => setHint(null), 2000);
  };

  const doneIds = Object.keys(done);
  const doneCount = doneIds.length;
  const complete = doneCount === OBJECTS.length;
  const avgIou = useMemo(() => {
    if (!doneCount) return 0;
    return doneIds.reduce((s, id) => s + done[id].iou, 0) / doneCount;
  }, [done, doneIds, doneCount]);

  const visible = mode === "compare" ? OBJECTS : OBJECTS.filter((o) => done[o.id]);
  const hotObj = OBJECTS.find((o) => o.id === hot);
  const hotAnnotated = hotObj && (mode === "compare" || done[hotObj.id]);

  const tagFor = (o) =>
    mode === "compare" ? `${o.label} ${o.conf}` : `${o.label} · IoU ${done[o.id]?.iou.toFixed(2)}`;

  const animateFrom = mode === "try"
    ? Object.fromEntries(doneIds.map((id) => [id, done[id].from]))
    : null;

  const hintObj = OBJECTS.find((o) => o.id === hint);

  return (
    <section className="da" aria-labelledby="da-title">
      <div className="da__inner">
        <header className="da__head">
          <div>
            <div className="da__eyebrow">
              <span className="da__eyebrow-dot" />
              <span>See It In Action</span>
            </div>
            <h2 id="da-title" className="da__title">
              From raw pixels to <span>model-ready data.</span>
            </h2>
          </div>
          <p className="da__intro">
            Drag across the frame to compare raw and annotated data, or switch
            to Try it and label the scene yourself.
          </p>
        </header>

        <div className="da__layout">
          <div className="da__frame">
            <div
              ref={stageRef}
              className={`da__stage${hot ? " has-hot" : ""}`}
              data-mode={mode}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
            >
              <div className="da__scene">
                <Scene />
              </div>

              {mode === "compare" ? (
                <>
                  <div className="da__overlay" style={{ clipPath: `inset(0 0 0 ${pos}%)` }}>
                    <Annotations
                      items={visible}
                      layers={layers}
                      hot={hot}
                      onHot={setHot}
                      tagFor={tagFor}
                    />
                  </div>

                  <div className="da__divider" style={{ left: `${pos}%` }}>
                    <button
                      type="button"
                      className="da__grip"
                      role="slider"
                      aria-label="Compare raw and annotated image"
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuenow={Math.round(pos)}
                      onKeyDown={onSliderKey}
                    >
                      <MoveHorizontal size={16} strokeWidth={2} />
                    </button>
                  </div>

                  <span className="da__chip da__chip--l">Raw</span>
                  <span className="da__chip da__chip--r">Annotated</span>
                </>
              ) : (
                <>
                  <Annotations
                    items={visible}
                    layers={layers}
                    hot={hot}
                    onHot={setHot}
                    tagFor={tagFor}
                    animateFrom={animateFrom}
                  />

                  {hintObj && (
                    <div
                      className="da__hint"
                      style={{
                        left: pct(hintObj.box[0] - 8, W),
                        top: pct(hintObj.box[1] - 8, H),
                        width: pct(hintObj.box[2] + 16, W),
                        height: pct(hintObj.box[3] + 16, H),
                      }}
                    />
                  )}

                  {draft && (
                    <div
                      className="da__draft"
                      style={{
                        left: pct(draft[0], W),
                        top: pct(draft[1], H),
                        width: pct(draft[2], W),
                        height: pct(draft[3], H),
                      }}
                    />
                  )}

                  {miss && (
                    <div
                      className="da__miss"
                      style={{
                        left: pct(miss.rect[0], W),
                        top: pct(miss.rect[1], H),
                        width: pct(miss.rect[2], W),
                        height: pct(miss.rect[3], H),
                      }}
                    >
                      <span>{miss.text}</span>
                    </div>
                  )}

                  <span className="da__chip da__chip--l">
                    {complete ? "Complete" : "Your turn"}
                  </span>

                  {complete && (
                    <div className="da__done">
                      All {OBJECTS.length} labelled · avg IoU {avgIou.toFixed(2)}
                    </div>
                  )}
                </>
              )}

              <span className="da__xh da__xh--x" aria-hidden="true" />
              <span className="da__xh da__xh--y" aria-hidden="true" />
              <span className="da__scan" aria-hidden="true" />
              <span className="da__bracket da__bracket--tl" aria-hidden="true" />
              <span className="da__bracket da__bracket--br" aria-hidden="true" />
              <span ref={coordRef} className="da__coords" aria-hidden="true">
                x 000  y 000
              </span>
            </div>
          </div>

          {/* ---------------- PANEL ---------------- */}
          <aside className="da__panel">
            <div className="da__seg" role="tablist" aria-label="Demo mode">
              {[
                { id: "compare", label: "Compare" },
                { id: "try", label: "Try it" },
              ].map((m) => (
                <button
                  key={m.id}
                  type="button"
                  role="tab"
                  aria-selected={mode === m.id}
                  className={mode === m.id ? "is-on" : ""}
                  onClick={() => switchMode(m.id)}
                >
                  {m.label}
                </button>
              ))}
            </div>

            <div className="da__group">
              <span className="da__group-label">Layers</span>
              <div className="da__chips">
                {LAYER_OPTIONS.map((l) => (
                  <button
                    key={l.id}
                    type="button"
                    aria-pressed={layers[l.id]}
                    className={layers[l.id] ? "is-on" : ""}
                    onClick={() => toggleLayer(l.id)}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="da__group">
              <div className="da__group-row">
                <span className="da__group-label">Objects</span>
                <span className="da__count">
                  {mode === "compare" ? OBJECTS.length : doneCount} / {OBJECTS.length}
                </span>
              </div>

              <ul className="da__list">
                {OBJECTS.map((o) => {
                  const isDone = mode === "compare" || done[o.id];
                  return (
                    <li key={o.id}>
                      <button
                        type="button"
                        className={`da__row${hot === o.id ? " is-hot" : ""}${isDone ? " is-done" : ""}`}
                        style={{ "--c": o.color }}
                        onPointerEnter={() => setHot(o.id)}
                        onPointerLeave={() => setHot(null)}
                        onFocus={() => setHot(o.id)}
                        onBlur={() => setHot(null)}
                      >
                        <span className="da__swatch" />
                        <span className="da__row-name">{o.label}</span>
                        <span className="da__row-meta">
                          {mode === "compare"
                            ? o.conf
                            : done[o.id]
                            ? `IoU ${done[o.id].iou.toFixed(2)}`
                            : "pending"}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="da__inspect" style={{ "--c": hotObj?.color || "#ff6b00" }}>
              {hotObj ? (
                <>
                  <div className="da__zoom">
                    <Scene
                      viewBox={`${hotObj.box[0] - 26} ${hotObj.box[1] - 26} ${hotObj.box[2] + 52} ${
                        hotObj.box[3] + 52
                      }`}
                    />
                    {hotAnnotated && <span className="da__zoom-box" />}
                  </div>
                  <div className="da__inspect-text">
                    <strong>{hotObj.label}</strong>
                    <em>{hotObj.type}</em>
                    <p>{hotObj.desc}</p>
                  </div>
                </>
              ) : (
                <p className="da__inspect-empty">
                  {mode === "compare"
                    ? "Hover an object to zoom in and see what the label means."
                    : "Click and drag on the image to draw a box around an object."}
                </p>
              )}
            </div>

            {mode === "try" && (
              <div className="da__actions">
                <button type="button" onClick={showHint} disabled={complete}>
                  <Lightbulb size={15} strokeWidth={1.8} />
                  Hint
                </button>
                <button type="button" onClick={reset} disabled={!doneCount}>
                  <RotateCcw size={15} strokeWidth={1.8} />
                  Reset
                </button>
              </div>
            )}

            {complete && mode === "try" && (
              <a href="/contact-us" className="da__cta">
                <span>Talk to Our Team</span>
                <ArrowUpRight size={17} strokeWidth={1.8} />
              </a>
            )}
          </aside>
        </div>
      </div>
    </section>
  );
};

export default DataAnnotationDemo;
